import { IncomeItem } from "@/services/financeiro";
import type { ComercialUnitRow } from "@/services/comercial";
import type { MultiSelectOption } from "@/components/ui/MultiSelect";

/* ─── Types de saída ─────────────────────────────────────────────────────── */

export interface MesReceitaStat {
  mes:     string;
  mesKey:  string;
  total:   number;
  pago:    number;
  aberto:  number;
  /** Casas vendidas no mês (mesmo critério do card: unidades distintas; fallback contratos). */
  casas:              number;
  /** Média de casas vendidas no mês — sempre inteiro. */
  mediaCasas:         number;
  precoMedioGeral:    number;
  precoMedioPreChave: number;
  items:   IncomeItem[];
}

export interface CentroCustoMedioStat {
  costCenterId: string;
  nome:         string;
  nomeAbrev:    string;
  quantidade:   number;
  valorTotal:   number;
  valorMedio:   number;
  fill:         string;
  items:        IncomeItem[];
}

export interface EvolucaoVendasStat {
  mes:                  string;
  mesKey:               string;
  vendas:               number;
  distratos:            number;
  vendasAcumuladas:     number;
  /** Acumulado bruto do período (antes de excluir distratados), quando informado. */
  vendasBrutasAcumuladas?: number;
  distratosAcumulados:  number;
  percentual:           number;
}

export interface ClienteStat {
  name:       string;
  valor:      number;
  quantidade: number;
  pago:       number;
  aberto:     number;
  items:      IncomeItem[];
}

export interface TipoDocStat {
  name:       string;
  valor:      number;
  quantidade: number;
  fill:       string;
  items:      IncomeItem[];
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

const MES_LABELS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
                    "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

const TIPO_COLORS = [
  "#8b5cf6", "#3b82f6", "#10b981", "#f59e0b",
  "#ef4444", "#06b6d4", "#f97316", "#84cc16",
  "#ec4899", "#6366f1", "#14b8a6", "#a78bfa",
];

function getMonthLabel(dateStr: string): string {
  if (!dateStr || dateStr.length < 7) return "—";
  const [yearStr, monthStr] = dateStr.substring(0, 7).split("-");
  const year  = (yearStr ?? "????").slice(2);
  const month = parseInt(monthStr ?? "0", 10) - 1;
  return `${MES_LABELS[month] ?? "??"}/${year}`;
}

function getMonthKey(dateStr: string): string {
  return dateStr?.substring(0, 7) ?? "";
}

const MONTH_KEY_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

function isValidMonthKey(mes: string): boolean {
  return MONTH_KEY_RE.test(mes);
}

function monthsInclusive(fromKey: string, toKey: string): number {
  const [anoInicial, mesInicial] = fromKey.split("-").map(Number);
  const [anoFinal, mesFinal] = toKey.split("-").map(Number);
  return (anoFinal - anoInicial) * 12 + mesFinal - mesInicial + 1;
}

function saleUnitKey(item: IncomeItem): string {
  return (item.buildingUnitId ?? "").trim() || (item.buildingUnitName ?? "").trim();
}

function isSoldStock(stock: string): boolean {
  const s = stock.trim().toUpperCase().normalize("NFD").replace(/\p{M}/gu, "");
  if (!s) return false;
  if (s === "V") return true;
  return /SOLD|VENDID|COMERCIALIZ/.test(s);
}

function isAvailableStock(stock: string): boolean {
  const s = stock.trim().toUpperCase().normalize("NFD").replace(/\p{M}/gu, "");
  if (!s) return false;
  if (s === "D") return true;
  return /AVAILABLE|DISPON|LIVRE|EM ESTOQUE/.test(s);
}

function unitMatchesSoldKey(unit: ComercialUnitRow, soldKeys: Set<string>): boolean {
  const id = unit.id.trim();
  const name = unit.name.trim();
  return Boolean((id && soldKeys.has(id)) || (name && name !== "—" && soldKeys.has(name)));
}

/** Ainda há casa para vender no escopo: estoque Sienge ou unidade cadastrada sem venda. */
function hasCasasDisponiveis(units: ComercialUnitRow[], soldKeys: Set<string>): boolean {
  if (units.length === 0) return false;

  const withStock = units.filter((u) => (u.commercialStock ?? "").trim() !== "");
  if (withStock.length > 0) {
    if (withStock.some((u) => isAvailableStock(u.commercialStock))) return true;
    if (withStock.every((u) => isSoldStock(u.commercialStock))) return false;
  }

  return units.some((unit) => {
    if (isSoldStock(unit.commercialStock ?? "")) return false;
    return !unitMatchesSoldKey(unit, soldKeys);
  });
}

function getEffectiveDate(item: IncomeItem): string {
  return item.dueDate || item.issueDate;
}

function abrev(name: string, max = 22): string {
  return name.length > max ? name.slice(0, max - 1) + "…" : name;
}

function displayNameFromCcLabel(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return trimmed;
  for (const sep of [" – ", " - ", "–", "-"]) {
    const i = trimmed.indexOf(sep);
    if (i > 0) {
      const before = trimmed.slice(0, i).trim();
      const after = trimmed.slice(i + sep.length).trim();
      if (!after) return before;
      // Código numérico (ex.: "49 – Recanto") → nome da obra
      if (/^\d+$/.test(before)) return after;
      // Código textual (ex.: "RIVIERA – 1º ETAPA") → nome inicial do cadastro
      return before;
    }
  }
  return trimmed;
}

function buildCostCenterNameLookup(
  ccOptions?: MultiSelectOption[],
  units?: ComercialUnitRow[],
): Map<string, string> {
  const m = new Map<string, string>();

  for (const u of units ?? []) {
    const name = (u.scopeLabel ?? "").trim();
    if (!name || name === "—") continue;
    for (const id of [u.costCenterId, u.enterpriseId]) {
      const key = (id ?? "").trim();
      if (!key) continue;
      m.set(key, name);
      const n = Number(key);
      if (!Number.isNaN(n)) m.set(String(n), name);
    }
  }

  for (const o of ccOptions ?? []) {
    const val = String(o.value ?? "").trim();
    const label = String(o.label ?? "").trim();
    if (!val || !label) continue;
    const displayName = displayNameFromCcLabel(label);
    m.set(val, displayName);
    const n = Number(val);
    if (!Number.isNaN(n)) m.set(String(n), displayName);
    for (const sep of [" – ", " - ", "–", "-"]) {
      const i = label.indexOf(sep);
      if (i > 0) {
        const code = label.slice(0, i).trim();
        if (code) {
          m.set(code, displayName);
          const cn = Number(code);
          if (!Number.isNaN(cn)) m.set(String(cn), displayName);
        }
        break;
      }
    }
  }

  return m;
}

function lookupCcName(id: string, lookup: Map<string, string>): string {
  const trimmed = id.trim();
  if (!trimmed) return "";
  if (lookup.has(trimmed)) return lookup.get(trimmed)!;
  const num = Number(trimmed);
  if (!Number.isNaN(num) && lookup.has(String(num))) return lookup.get(String(num))!;
  const stripped = trimmed.replace(/^0+/, "") || "0";
  if (stripped !== trimmed && lookup.has(stripped)) return lookup.get(stripped)!;
  if (lookup.has(stripped)) return lookup.get(stripped)!;
  return "";
}

function isLikelyCostCenterCode(name: string, id: string): boolean {
  const n = name.trim();
  const i = id.trim();
  if (!n) return true;
  if (n === i) return true;
  if (/^\d+$/.test(n) && /^\d+$/.test(i) && Number(n) === Number(i)) return true;
  return false;
}

function resolveCostCenterKey(
  item: IncomeItem,
  unitCcMap?: Map<string, { id: string; name: string }>,
  ccLookup?: Map<string, string>
): { id: string; name: string } {
  const lookup = ccLookup ?? new Map();
  const directId = (item.costCenterId ?? "").trim();
  const directName = (item.costCenterName ?? "").trim();
  if (directId) {
    const lookedUp = lookupCcName(directId, lookup);
    const fromItem = isLikelyCostCenterCode(directName, directId) ? "" : directName;
    return {
      id: directId,
      name: lookedUp || fromItem || directId,
    };
  }

  const unitId = (item.buildingUnitId ?? "").trim();
  if (unitId && unitCcMap?.has(unitId)) {
    const cc = unitCcMap.get(unitId)!;
    const lookedUp = lookupCcName(cc.id, lookup);
    const fromUnit = isLikelyCostCenterCode(cc.name, cc.id) ? "" : cc.name;
    return {
      id: cc.id,
      name: lookedUp || fromUnit || cc.id,
    };
  }

  return { id: "__unknown__", name: "Não informado" };
}

function buildUnitCostCenterMap(units: ComercialUnitRow[]): Map<string, { id: string; name: string }> {
  const map = new Map<string, { id: string; name: string }>();
  for (const u of units) {
    const ccId = (u.costCenterId ?? "").trim();
    if (!ccId) continue;
    const ccName = (u.scopeLabel ?? "").trim() || ccId;
    map.set(u.id, { id: ccId, name: ccName });
  }
  return map;
}

/** Nome para ranking: ignora placeholder; usa cliente, unidade, descrição ou contrato. */
function clienteRankingKey(item: IncomeItem): string {
  const n = item.clientName?.trim();
  const placeholder =
    !n ||
    n === "—" ||
    n === "-" ||
    n === "–" ||
    /^cliente\s+n[aã]o\s+informado$/i.test(n);
  if (!placeholder) return n;
  const u = item.buildingUnitName?.trim();
  if (u) return u;
  const d = item.description?.trim();
  if (d) return d.length > 80 ? `${d.slice(0, 80)}…` : d;
  const id = item.billId?.trim();
  if (id) return `Contrato ${id}`;
  return "Cliente não informado";
}

/* ─── Computações ────────────────────────────────────────────────────────── */

function billKey(item: IncomeItem): string {
  return (item.billId ?? "").trim();
}

function isSyntheticBillId(id: string): boolean {
  return /^sale-\d+$/.test(id);
}

/**
 * Remove vendas cujo contrato foi distratado até a data da pesquisa.
 * A venda some no mês original; distratos não criam nem aumentam meses sem venda.
 */
export function excludeVendasDistratadas(
  vendas: IncomeItem[],
  distratosAtePesquisa: IncomeItem[],
  researchDate = "",
): IncomeItem[] {
  const limite = researchDate.trim().slice(0, 10);

  const cancelados = new Set<string>();
  for (const d of distratosAtePesquisa) {
    const cancelOn = (d.cancellationDate ?? "").trim().slice(0, 10);
    if (!cancelOn) continue;
    if (limite && cancelOn > limite) continue;
    const id = billKey(d);
    if (id && !isSyntheticBillId(id)) cancelados.add(id);
  }

  return vendas.filter((item) => {
    const cancelOn = (item.cancellationDate ?? "").trim().slice(0, 10);
    if (cancelOn && (!limite || cancelOn <= limite)) return false;
    const id = billKey(item);
    if (id && cancelados.has(id)) return false;
    return true;
  });
}

export function computeReceitasPorMes(income: IncomeItem[]): MesReceitaStat[] {
  const map = new Map<string, MesReceitaStat>();

  for (const item of income) {
    const date = getEffectiveDate(item);
    const key  = getMonthKey(date);
    if (!key) continue;

    if (!map.has(key)) {
      map.set(key, {
        mes: getMonthLabel(date), mesKey: key, total: 0, pago: 0, aberto: 0,
        casas: 0, mediaCasas: 0, precoMedioGeral: 0, precoMedioPreChave: 0, items: [],
      });
    }
    const m = map.get(key)!;
    m.total += item.originalAmount;
    if (item.isPaid) m.pago  += item.originalAmount;
    else             m.aberto += item.originalAmount;
    m.items.push(item);
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, v]) => {
      const casas = countCasasVendidas(v.items).count;
      const mediaCasas = Math.round(casas);
      const preChave = v.items.reduce((s, i) => s + (i.preChaveAmount ?? 0), 0);
      return {
        ...v,
        casas: mediaCasas,
        mediaCasas,
        precoMedioGeral:    mediaCasas > 0 ? v.total / mediaCasas : 0,
        precoMedioPreChave: mediaCasas > 0 ? preChave / mediaCasas : 0,
      };
    });
}

export interface VelocidadeVendasStat {
  value: number;
  /** True quando ainda há unidades cadastradas sem venda até a data da pesquisa. */
  estoqueAberto: boolean;
}

/**
 * Velocidade de vendas: unidades distintas vendidas divididas pelos meses
 * corridos desde a primeira venda, inclusive meses sem venda.
 *
 * O fim do intervalo depende do estoque:
 * - ainda há casas disponíveis → até o mês da data da pesquisa;
 * - estoque esgotado → até o mês da última casa vendida.
 */
export function computeMediaCasasVendidas(
  income: IncomeItem[],
  units: ComercialUnitRow[] = [],
  researchDate = "",
): VelocidadeVendasStat {
  const researchDay = researchDate.trim().slice(0, 10);
  const vendasPorUnidade = new Map<string, string[]>();
  const soldUnitKeys = new Set<string>();

  for (const item of income) {
    const date = getEffectiveDate(item);
    const day = date.trim().slice(0, 10);
    if (researchDay && day && day > researchDay) continue;

    const mes = getMonthKey(date);
    if (!isValidMonthKey(mes)) continue;

    const unitId = (item.buildingUnitId ?? "").trim();
    const unitName = (item.buildingUnitName ?? "").trim();
    const billId = (item.billId ?? "").trim();
    const chave = unitId || unitName || billId;
    if (!chave) continue;

    const vendas = vendasPorUnidade.get(chave) ?? [];
    vendas.push(mes);
    vendasPorUnidade.set(chave, vendas);

    const unitKey = saleUnitKey(item);
    if (unitKey) soldUnitKeys.add(unitKey);
  }

  if (vendasPorUnidade.size === 0) {
    return { value: 0, estoqueAberto: false };
  }

  const mesesDeVenda = Array.from(vendasPorUnidade.values()).flat();
  const primeiroMes = mesesDeVenda.reduce((menor, mes) => mes < menor ? mes : menor);
  const ultimoMesVenda = mesesDeVenda.reduce((maior, mes) => mes > maior ? mes : maior);
  const estoqueAberto = hasCasasDisponiveis(units, soldUnitKeys);
  const researchMonth = getMonthKey(researchDay);
  const ultimoMes = estoqueAberto && isValidMonthKey(researchMonth)
    ? (researchMonth < primeiroMes ? primeiroMes : researchMonth)
    : ultimoMesVenda;
  const mesesCorridos = Math.max(1, monthsInclusive(primeiroMes, ultimoMes));

  return { value: vendasPorUnidade.size / mesesCorridos, estoqueAberto };
}

export interface VelocidadeVendasMesRow {
  mes: string;
  mesKey: string;
  vendasTotais: number;
  distratos: number;
  vendasLiquidas: number;
}

function contractKey(item: IncomeItem): string {
  return (item.buildingUnitId ?? "").trim()
    || (item.buildingUnitName ?? "").trim()
    || (item.billId ?? "").trim();
}

function countContratosPorMes(
  items: IncomeItem[],
  getDate: (item: IncomeItem) => string | undefined,
  researchDay: string,
): Map<string, number> {
  const contratos = new Map<string, string>();

  for (const item of items) {
    const date = (getDate(item) ?? "").trim();
    const day = date.slice(0, 10);
    if (researchDay && day && day > researchDay) continue;
    const mes = getMonthKey(date);
    if (!isValidMonthKey(mes)) continue;
    const key = contractKey(item);
    if (!key) continue;
    const existing = contratos.get(key);
    if (!existing || mes < existing) contratos.set(key, mes);
  }

  const result = new Map<string, number>();
  for (const mes of contratos.values()) {
    result.set(mes, (result.get(mes) ?? 0) + 1);
  }
  return result;
}

/**
 * Detalhe mensal da velocidade de vendas no mesmo ciclo do card:
 * da primeira venda até a data da pesquisa (estoque aberto) ou até a última venda.
 */
export function computeVelocidadeVendasMensal(
  vendas: IncomeItem[],
  distratos: IncomeItem[],
  units: ComercialUnitRow[] = [],
  researchDate = "",
): VelocidadeVendasMesRow[] {
  const researchDay = researchDate.trim().slice(0, 10);
  const vendasPorMes = countContratosPorMes(vendas, getEffectiveDate, researchDay);
  const distratosPorMes = countContratosPorMes(distratos, (item) => item.cancellationDate, researchDay);
  const ciclo = computeMediaCasasVendidas(vendas, units, researchDate);

  const mesesVenda = [...vendasPorMes.keys()].sort();
  if (mesesVenda.length === 0) return [];

  const primeiroMes = mesesVenda[0];
  const ultimoMesVenda = mesesVenda[mesesVenda.length - 1];
  const researchMonth = getMonthKey(researchDay);
  const ultimoMes = ciclo.estoqueAberto && isValidMonthKey(researchMonth)
    ? (researchMonth < primeiroMes ? primeiroMes : researchMonth)
    : ultimoMesVenda;
  const quantidadeMeses = Math.max(1, monthsInclusive(primeiroMes, ultimoMes));
  const [anoInicial, mesInicial] = primeiroMes.split("-").map(Number);

  return Array.from({ length: quantidadeMeses }, (_, index) => {
    const data = new Date(anoInicial, mesInicial - 1 + index, 1);
    const mesKey = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}`;
    const mes = `${MES_LABELS[data.getMonth()]}/${String(data.getFullYear()).slice(2)}`;
    const vendasTotais = vendasPorMes.get(mesKey) ?? 0;
    const qtdDistratos = distratosPorMes.get(mesKey) ?? 0;
    return {
      mes,
      mesKey,
      vendasTotais,
      distratos: qtdDistratos,
      vendasLiquidas: vendasTotais - qtdDistratos,
    };
  });
}

function unitSaleKey(item: IncomeItem): string {
  return (
    (item.buildingUnitId ?? "").trim() ||
    (item.buildingUnitName ?? "").trim()
  );
}

/**
 * Evolução das vendas válidas no período em relação à meta de unidades cadastradas.
 * O percentual usa vendas válidas (sem distratados); distratos do período aparecem
 * no detalhe mensal/acumulado para transparência (alinhado ao card de Distratos).
 */
export function computeEvolucaoVendas(
  vendas: IncomeItem[],
  distratos: IncomeItem[],
  metaUnidades: number,
  vendasReferencia?: IncomeItem[],
): EvolucaoVendasStat[] {
  if (metaUnidades <= 0) return [];

  const eventosPorMes = (
    items: IncomeItem[],
    getDate: (item: IncomeItem) => string | undefined
  ): Map<string, number> => {
    const contratos = new Map<string, string>();

    for (const item of items) {
      const date = getDate(item) ?? "";
      const mes = getMonthKey(date);
      if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(mes)) continue;
      const key =
        unitSaleKey(item) ||
        billKey(item);
      if (!key) continue;
      const existing = contratos.get(key);
      if (!existing || mes < existing) contratos.set(key, mes);
    }

    const result = new Map<string, number>();
    for (const mes of contratos.values()) {
      result.set(mes, (result.get(mes) ?? 0) + 1);
    }
    return result;
  };

  const vendasPorMes = eventosPorMes(vendas, getEffectiveDate);
  const vendasBrutasPorMes = vendasReferencia
    ? eventosPorMes(vendasReferencia, getEffectiveDate)
    : null;
  const distratosPorMes = eventosPorMes(distratos, (item) => item.cancellationDate);
  const meses = [...vendasPorMes.keys(), ...distratosPorMes.keys()].sort();
  if (meses.length === 0) return [];

  const primeiroMes = meses[0];
  const ultimoMes = meses[meses.length - 1];
  const [anoInicial, mesInicial] = primeiroMes.split("-").map(Number);
  const [anoFinal, mesFinal] = ultimoMes.split("-").map(Number);
  const quantidadeMeses = (anoFinal - anoInicial) * 12 + mesFinal - mesInicial + 1;

  let vendasAcumuladas = 0;
  let vendasBrutasAcumuladas = 0;
  let distratosAcumulados = 0;
  return Array.from({ length: quantidadeMeses }, (_, index) => {
    const data = new Date(anoInicial, mesInicial - 1 + index, 1);
    const mesKey = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}`;
    const mes = `${MES_LABELS[data.getMonth()]}/${String(data.getFullYear()).slice(2)}`;
    const vendasNoMes = vendasPorMes.get(mesKey) ?? 0;
    const distratosNoMes = distratosPorMes.get(mesKey) ?? 0;
    vendasAcumuladas += vendasNoMes;
    if (vendasBrutasPorMes) {
      vendasBrutasAcumuladas += vendasBrutasPorMes.get(mesKey) ?? 0;
    }
    distratosAcumulados += distratosNoMes;

    return {
      mes,
      mesKey,
      vendas: vendasNoMes,
      distratos: distratosNoMes,
      vendasAcumuladas,
      vendasBrutasAcumuladas: vendasBrutasPorMes ? vendasBrutasAcumuladas : undefined,
      distratosAcumulados,
      percentual: Math.max(0, Math.min(100, (vendasAcumuladas / metaUnidades) * 100)),
    };
  });
}

export function computeValorMedioPorCentroCusto(
  income: IncomeItem[],
  ccOptions?: MultiSelectOption[],
  units?: ComercialUnitRow[]
): CentroCustoMedioStat[] {
  const unitCcMap = units?.length ? buildUnitCostCenterMap(units) : undefined;
  const ccLookup  = buildCostCenterNameLookup(ccOptions, units);
  const map = new Map<string, CentroCustoMedioStat>();

  for (const item of income) {
    const cc = resolveCostCenterKey(item, unitCcMap, ccLookup);
    if (!map.has(cc.id)) {
      map.set(cc.id, {
        costCenterId: cc.id,
        nome:         cc.name,
        nomeAbrev:    abrev(cc.name),
        quantidade:   0,
        valorTotal:   0,
        valorMedio:   0,
        fill:         TIPO_COLORS[map.size % TIPO_COLORS.length],
        items:        [],
      });
    }
    const s = map.get(cc.id)!;
    s.quantidade += 1;
    s.valorTotal += item.originalAmount;
    s.items.push(item);
  }

  return Array.from(map.values())
    .map((s) => ({
      ...s,
      valorMedio: s.quantidade > 0 ? s.valorTotal / s.quantidade : 0,
    }))
    .sort((a, b) => b.valorMedio - a.valorMedio);
}

export function computeRankingClientes(income: IncomeItem[]): ClienteStat[] {
  const map = new Map<string, ClienteStat>();

  for (const item of income) {
    const key = clienteRankingKey(item);
    if (!map.has(key)) {
      map.set(key, { name: key, valor: 0, quantidade: 0, pago: 0, aberto: 0, items: [] });
    }
    const s = map.get(key)!;
    s.valor      += item.originalAmount;
    s.quantidade += 1;
    if (item.isPaid) s.pago  += item.originalAmount;
    else             s.aberto += item.originalAmount;
    s.items.push(item);
  }

  return Array.from(map.values())
    .sort((a, b) => b.valor - a.valor)
    .slice(0, 10);
}

/** Ranking de corretores / imobiliárias a partir de `brokers[].name` (bulk sales). */
export function computeRankingBrokers(
  income: IncomeItem[],
  limit = 10,
): ClienteStat[] {
  const map = new Map<string, ClienteStat>();
  const unitKeysByBroker = new Map<string, Set<string>>();

  for (const item of income) {
    const key = (item.brokerName ?? "").trim() || "Não informado";
    if (!map.has(key)) {
      map.set(key, { name: key, valor: 0, quantidade: 0, pago: 0, aberto: 0, items: [] });
      unitKeysByBroker.set(key, new Set());
    }
    const s = map.get(key)!;
    s.valor      += item.originalAmount;
    if (item.isPaid) s.pago  += item.originalAmount;
    else             s.aberto += item.originalAmount;
    s.items.push(item);

    const unitId = (item.buildingUnitId ?? "").trim();
    const unitName = (item.buildingUnitName ?? "").trim();
    const billId = (item.billId ?? "").trim();
    const unitKey = unitId || unitName || billId;
    if (unitKey) unitKeysByBroker.get(key)!.add(unitKey);
  }

  return Array.from(map.values())
    .map((broker) => ({
      ...broker,
      quantidade: unitKeysByBroker.get(broker.name)?.size ?? 0,
    }))
    .sort((a, b) => b.valor - a.valor)
    .slice(0, limit);
}

export function computeReceitasPorTipo(income: IncomeItem[]): TipoDocStat[] {
  const map = new Map<string, TipoDocStat>();

  for (const item of income) {
    const key = item.documentType?.trim() || "Sem Tipo";
    if (!map.has(key)) {
      map.set(key, {
        name:       key,
        valor:      0,
        quantidade: 0,
        fill:       TIPO_COLORS[map.size % TIPO_COLORS.length],
        items:      [],
      });
    }
    const s = map.get(key)!;
    s.valor      += item.originalAmount;
    s.quantidade += 1;
    s.items.push(item);
  }

  return Array.from(map.values())
    .sort((a, b) => b.valor - a.valor)
    .slice(0, 8);
}

export interface CasaVendidaRow {
  key:     string;
  unidade: string;
  cliente: string;
  obra:    string;
  data:    string;
  valor:   number;
  items:   IncomeItem[];
}

/**
 * Lista das casas vendidas — mesmo agrupamento de `countCasasVendidas`
 * (unidades distintas; fallback: contratos/billId), com obra resolvida como nos gráficos.
 */
export function computeCasasVendidasDetalhe(
  income: IncomeItem[],
  ccOptions?: MultiSelectOption[],
  units?: ComercialUnitRow[]
): CasaVendidaRow[] {
  const unitCcMap = units?.length ? buildUnitCostCenterMap(units) : undefined;
  const ccLookup  = buildCostCenterNameLookup(ccOptions, units);
  const hasUnits = income.some(
    (i) => (i.buildingUnitId ?? "").trim() || (i.buildingUnitName ?? "").trim()
  );
  const map = new Map<string, CasaVendidaRow>();

  for (const item of income) {
    const id = (item.buildingUnitId ?? "").trim();
    const nm = (item.buildingUnitName ?? "").trim();
    const key = hasUnits ? id || nm : (item.billId ?? "").trim();
    if (!key) continue;

    if (!map.has(key)) {
      const cc = resolveCostCenterKey(item, unitCcMap, ccLookup);
      map.set(key, {
        key,
        unidade: nm || (id ? `Unidade ${id}` : `Contrato ${item.billId}`),
        cliente: clienteRankingKey(item),
        obra:    cc.id === "__unknown__" ? "—" : cc.name,
        data:    item.issueDate || item.dueDate || "",
        valor:   0,
        items:   [],
      });
    }
    const r = map.get(key)!;
    // Mesmo critério do Preço Médio Geral: originalAmount − condição DE
    r.valor += item.originalAmount - (item.discountAmount ?? 0);
    r.items.push(item);
  }

  return Array.from(map.values()).sort((a, b) => b.valor - a.valor);
}

/** Unidades distintas com receita; se o Sienge não enviar unidade, usa títulos (billId) distintos como aproximação. */
export function countCasasVendidas(
  income: IncomeItem[]
): { count: number; source: "units" | "contracts" } {
  const unitKeys = new Set<string>();
  for (const i of income) {
    const id = (i.buildingUnitId ?? "").trim();
    const nm = (i.buildingUnitName ?? "").trim();
    const k = id || nm;
    if (k) unitKeys.add(k);
  }
  if (unitKeys.size > 0) {
    return { count: unitKeys.size, source: "units" };
  }
  const bills = new Set<string>();
  for (const i of income) {
    const b = (i.billId ?? "").trim();
    if (b) bills.add(b);
  }
  return { count: bills.size, source: "contracts" };
}

/** Contratos cancelados (distratos) distintos no período. */
export function countDistratos(distratos: IncomeItem[]): number {
  const ids = new Set<string>();
  for (const d of distratos) {
    const id = (d.billId ?? "").trim();
    if (id) ids.add(id);
  }
  return ids.size;
}

export interface DistratoRow {
  key:     string;
  unidade: string;
  cliente: string;
  obra:    string;
  data:    string;
  valor:   number;
  motivo:  string;
  items:   IncomeItem[];
}

/**
 * Histórico de distratos — um registro por contrato (billId),
 * alinhado a `countDistratos`, com obra resolvida como nos gráficos.
 */
export function computeDistratosDetalhe(
  distratos: IncomeItem[],
  ccOptions?: MultiSelectOption[],
  units?: ComercialUnitRow[]
): DistratoRow[] {
  const unitCcMap = units?.length ? buildUnitCostCenterMap(units) : undefined;
  const ccLookup  = buildCostCenterNameLookup(ccOptions, units);
  const map = new Map<string, DistratoRow>();

  for (const item of distratos) {
    const key = (item.billId ?? "").trim();
    if (!key) continue;

    if (!map.has(key)) {
      const id = (item.buildingUnitId ?? "").trim();
      const nm = (item.buildingUnitName ?? "").trim();
      const cc = resolveCostCenterKey(item, unitCcMap, ccLookup);
      map.set(key, {
        key,
        unidade: nm || (id ? `Unidade ${id}` : `Contrato ${key}`),
        cliente: clienteRankingKey(item),
        obra:    cc.id === "__unknown__" ? "—" : cc.name,
        data:    item.cancellationDate || "",
        valor:   0,
        motivo:  (item.cancellationReason ?? "").trim() || "—",
        items:   [],
      });
    }
    const r = map.get(key)!;
    r.valor += item.originalAmount;
    r.items.push(item);
    if (!r.data && item.cancellationDate) r.data = item.cancellationDate;
    if (r.motivo === "—" && (item.cancellationReason ?? "").trim()) {
      r.motivo = item.cancellationReason!.trim();
    }
  }

  return Array.from(map.values()).sort((a, b) => {
    if (a.data && b.data && a.data !== b.data) return b.data.localeCompare(a.data);
    return b.valor - a.valor;
  });
}
